// Libraries
import { action, computed, observable } from "mobx";
import moment from "moment";

// Core
import { FieldType, isEmptyOrWhitespace } from "Core/Utils/Utils";
import { ViewModelBase } from "Core/ViewModels";
import { isNullOrUndefined } from "util";

// Custom
import { Server } from "Custom/Globals/AppUrls";
import { PropertyModel, PropertyModelDTO } from "Custom/Models/DashboardModels/PropertyModel";
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { StoresInstance } from "Custom/Stores/Stores";
import { GlobalSetting } from "Custom/Models";
import { ProductSourceType } from "Custom/Models/Enums/ProductSource";
import { OutstandingPayment } from "Custom/Models/DashboardModels/outstandingPayment";
import { CompletedPayment } from "Custom/Models/DashboardModels/completedPayment";

export class PropertyViewModel extends ViewModelBase<PropertyModel> {
    constructor(propertyId: string) {
        super(new PropertyModel());
        this.setDecorators(PropertyModel);

        this.propertyId = propertyId;
        this.loggedInUserEmail = StoresInstance.domain.AccountStore ? StoresInstance.domain.AccountStore.DisplayName : "";

        if (propertyId !== null && propertyId !== undefined && propertyId !== "") {
            this.load(propertyId);
            this.loadUserSignatureRole(propertyId, this.loggedInUserEmail);
        }
    }

    // #region Properties

    @observable
    public propertyId: string = "";

    @observable
    public loggedInUserEmail: string = "";

    @observable
    public apiStatus: ApiStatus = ApiStatus.loading;

    @observable
    public selectedTotalToPay: number = 0;

    @observable
    public masterOutstandingPaymentsList: OutstandingPayment[] = [];

    @observable
    public masterCompletedPaymentsList: CompletedPayment[] = [];

    @observable
    public chosenOutstandingPaymentItem: OutstandingPayment[] = [];

    // #endregion Properties

    // #region Computeds

    @computed
    public get Property() {
        return this.model;
    }

    @computed
    public get ApiStatus(): ApiStatus {
        return this.apiStatus;
    }

    @computed
    public get PropertyAddress() {
        let address = [this.model.addressLine1, this.model.addressLine2, this.model.postcode].filter(i => !isEmptyOrWhitespace(i)).join(", ");

        return isEmptyOrWhitespace(address) ? "No address available for this plot" : address;
    }

    @computed
    public get showStripePaymentRequest() {
        const setting = StoresInstance.domain.AccountStore.getSettingByType(22);

        if (setting) {
            if (setting.settingValue === "0") {
                return false;
            } else {
                return true;
            }
        }

        return false;
    }

    @computed
    public get showFullReservationFormV2() {
        const setting = StoresInstance.domain.AccountStore.getSettingByType(27);

        if (setting) {
            if (setting.settingValue === "0") {
                return false;
            } else {
                return true;
            }
        }

        return false;
    }

    @computed
    public get currentUserId() {
        return StoresInstance.domain.AccountStore.Id;
    }

    @computed
    public get hasStripePaymentRequest() {
        const hasPendingPaymentRequests = this.model.stripePaymentRequests.filter(p => p.hasPaid === false && p.propertyId === this.model.id);

        if (hasPendingPaymentRequests) {
            return hasPendingPaymentRequests.length > 0;
        }
        return false;
    }

    @computed
    public get hasPaidStripeReservationPaymentRequest() {
        const hasPaidPendingPaymentRequest = this.model.stripePaymentRequests.find(p => p.hasPaid === true && p.propertyId === this.model.id && p.type === 1);

        if (hasPaidPendingPaymentRequest) {
            return true;
        }
        return false;
    }

    @computed
    public get PaidReservationDate() {
        const hasPaidPendingPaymentRequest = this.model.stripePaymentRequests.find(p => p.hasPaid === true && p.propertyId === this.model.id && p.type === 1);

        if (hasPaidPendingPaymentRequest) {
            let paidDate = moment(hasPaidPendingPaymentRequest.paidDate);
            return paidDate.format("DD/MM/YYYY");
        }

        return "";
    }

    @computed
    public get PaidOrderDate() {
        const hasPaidPendingPaymentRequest = this.model.stripePaymentRequests.find(p => p.hasPaid === true && p.propertyId === this.model.id && p.type === 2);

        if (hasPaidPendingPaymentRequest) {
            let paidDate = moment(hasPaidPendingPaymentRequest.paidDate);
            return paidDate.format("DD/MM/YYYY");
        }

        return "";
    }

    @computed
    public get hasPaidStripeOrderPaymentRequest() {
        const hasPaidPendingPaymentRequest = this.model.stripePaymentRequests.find(p => p.hasPaid === true && p.propertyId === this.model.id && p.type === 2);

        if (hasPaidPendingPaymentRequest) {
            return true;
        }
        return false;
    }

    @computed
    public get getReservationStripePaymentRequest() {
        const hasPendingPaymentRequest = this.model.stripePaymentRequests.find(p => p.propertyId === this.model.id && p.type === 1);

        if (hasPendingPaymentRequest) {
            return `£${hasPendingPaymentRequest.payment.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
        }
        return "-";
    }

    @computed
    public get getReservationFeeValue() {
        const hasPendingPaymentRequest = this.model.stripePaymentRequests.find(p => p.propertyId === this.model.id && p.type === 1);

        return hasPendingPaymentRequest ? hasPendingPaymentRequest.payment : 0;
    }

    @computed
    public get getTotalToPayString() {
        return `£${this.selectedTotalToPay.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
    }

    @computed
    public get getTotalToPay() {
        return this.selectedTotalToPay;
    }

    // computed return combined list of reservation and choices outstanding payment requests
    @computed
    public get getOutstandingPaymentRequests() {
        const hasPendingPaymentRequests = this.model.stripePaymentRequests.filter(p => p.hasPaid === false && p.propertyId === this.model.id);
        return hasPendingPaymentRequests;
    }

    @computed
    public get getOutstandingChoicesAndBespokePaymentRequests() {
        const hasPendingPaymentRequests = this.model.stripePaymentRequests.filter(p => p.hasPaid === false && p.propertyId === this.model.id && (p.type === 2 || p.type === 3));
        return hasPendingPaymentRequests;
    }

    @computed
    public get getPropertyHouseTypeGeneralImages() {
        const images = this.model.propertyHouseTypeImages.filter(image => image.imageTypeName === "General");

        let i = 1;
        let imageLength = images.sort((a, b) => b.ordinal - a.ordinal)[0];
        let HouseTypeDevelopmentImage = [
            {
                imageUrl: "",
                caption: "",
                thumbnailImageUrl: "",
                ordinal: 0,
            },
        ];

        if (imageLength && imageLength.ordinal) {
            while (i <= imageLength.ordinal) {
                if (images && images.length > 0) {
                    const image = this.getHouseTypeImageWithOrdinal(i, "General");
                    if (image && image.imageUrl.length > 0) {
                        HouseTypeDevelopmentImage.push({
                            ...image,
                            imageUrl: image.imageUrl,
                            caption: image.caption,
                            thumbnailImageUrl: image.thumbnailImageUrl,
                            ordinal: image.imageOrdinal,
                        });
                    }
                    const propertyImage = this.getPropertyImage("General", i);

                    if (propertyImage && propertyImage.imageUrl) {
                        HouseTypeDevelopmentImage.push({
                            imageUrl: propertyImage.imageUrl,
                            caption: propertyImage.caption,
                            thumbnailImageUrl: propertyImage.thumbnailImageUrl,
                            ordinal: propertyImage.imageOrdinal,
                        });
                    }
                }
                i++;
            }
        }

        return HouseTypeDevelopmentImage;
    }

    @computed
    public get getPropertyHouseTypeFloorplanImages() {
        const images = this.model.propertyHouseTypeImages.filter(image => image.imageTypeName === "Floorplan");

        let i = 1;
        let imageLength = images.sort((a, b) => b.ordinal - a.ordinal)[0];
        let HouseTypeDevelopmentImage = [
            {
                imageUrl: "",
                caption: "",
                thumbnailImageUrl: "",
                ordinal: 0,
            },
        ];

        if (imageLength && imageLength.ordinal) {
            while (i <= imageLength.ordinal) {
                if (images && images.length > 0) {
                    const image = this.getHouseTypeImageWithOrdinal(i, "Floorplan");
                    if (image && image.imageUrl.length > 0) {
                        HouseTypeDevelopmentImage.push({
                            ...image,
                            imageUrl: image.imageUrl,
                            caption: image.caption,
                            thumbnailImageUrl: image.thumbnailImageUrl,
                            ordinal: image.imageOrdinal,
                        });
                    }

                    const propertyImage = this.getPropertyImage("Floorplan", i);

                    if (propertyImage && propertyImage.imageUrl) {
                        HouseTypeDevelopmentImage.push({
                            imageUrl: propertyImage.imageUrl,
                            caption: propertyImage.caption,
                            thumbnailImageUrl: propertyImage.thumbnailImageUrl,
                            ordinal: propertyImage.imageOrdinal,
                        });
                    }
                }
                i++;
            }
        }

        return HouseTypeDevelopmentImage;
    }

    @computed
    public get getDevelopmentSiteMapImages() {
        const images = this.model.developmentSiteMapImages;

        if (!images || images.length === 0) return [];

        const developmentSiteMapImage = images
            .filter(image => image && image.imageUrl && image.imageUrl.length > 0)
            .sort((a, b) => b.ordinal - a.ordinal)
            .map(image => ({
                developmentId: image.developmentId,
                imageUrl: image.imageUrl,
                originalImageUrl: image.originalImageUrl || image.imageUrl,
                thumbnailImageUrl: image.thumbnailImageUrl || image.imageUrl,
                ordinal: image.ordinal,
                id: image.id,
            }));

        return developmentSiteMapImage;
    }

    @action
    private getHouseTypeImageWithOrdinal(ordinal: number, imageType: string) {
        const { propertyHouseTypeImages, houseTypeId, developmentId } = this.model;
        let imageUrl = "";
        let caption = "";
        let thumbnailImageUrl = "";
        let imageOrdinal = 0;

        if (propertyHouseTypeImages && propertyHouseTypeImages.length > 0) {
            const developmentImages = propertyHouseTypeImages.filter(
                htdi =>
                    htdi.houseTypeId === houseTypeId &&
                    htdi.developmentId === developmentId &&
                    htdi.ordinal === ordinal &&
                    htdi.imageTypeName === imageType &&
                    htdi.imageUrl.length > 0,
            );
            if (developmentImages && developmentImages.length > 0) {
                imageUrl = developmentImages[0].imageUrl;
                caption = developmentImages[0].caption;
                thumbnailImageUrl = developmentImages[0].thumbnailImageUrl;
                imageOrdinal = developmentImages[0].ordinal;
                if (imageUrl) return { imageUrl, caption, thumbnailImageUrl, imageOrdinal };
            }
            const houseTypeWithAllImage: { imageUrl: string; caption: string; thumbnailImageUrl: string; imageOrdinal: number } = this.gethouseTypeAllImages(ordinal, imageType);
            imageUrl = houseTypeWithAllImage.imageUrl;
            caption = houseTypeWithAllImage.caption;
            thumbnailImageUrl = houseTypeWithAllImage.thumbnailImageUrl;
            imageOrdinal = houseTypeWithAllImage.imageOrdinal;
        }

        return { imageUrl, caption, thumbnailImageUrl, imageOrdinal };
    }

    @action
    private gethouseTypeAllImages(ordinal: number, imageType: string) {
        const { propertyHouseTypeImages, houseTypeId } = this.model;
        let imageUrl = "",
            caption = "",
            thumbnailImageUrl = "",
            imageOrdinal = 0;

        if (propertyHouseTypeImages && propertyHouseTypeImages.length > 0) {
            const housetypeImages = propertyHouseTypeImages.filter(
                htdi => htdi.houseTypeId === houseTypeId && htdi.developmentId === null && htdi.ordinal === ordinal && htdi.imageTypeName === imageType,
            );
            if (housetypeImages && housetypeImages.length > 0) {
                imageUrl = housetypeImages[0].imageUrl;
                caption = housetypeImages[0].caption;
                thumbnailImageUrl = housetypeImages[0].thumbnailImageUrl;
                imageOrdinal = housetypeImages[0].ordinal;
            }
        }
        return { imageUrl, caption, thumbnailImageUrl, imageOrdinal };
    }

    @action
    private getPropertyImage(imageType: string, ordinal: number) {
        const { propertyHouseTypeImages } = this.model;
        let imageUrl = "",
            caption = "",
            thumbnailImageUrl = "",
            imageOrdinal = 0;

        if (propertyHouseTypeImages && propertyHouseTypeImages.length > 0) {
            const propertyImages = propertyHouseTypeImages
                .filter(htdi => isNullOrUndefined(htdi.houseTypeId) && htdi.imageTypeName == imageType && htdi.ordinal == ordinal && htdi.imageUrl.length > 0)
                .sort((a, b) => a.ordinal - b.ordinal);
            if (propertyImages && propertyImages.length > 0) {
                imageUrl = propertyImages[0].imageUrl;
                caption = propertyImages[0].caption;
                thumbnailImageUrl = propertyImages[0].thumbnailImageUrl;
                imageOrdinal = propertyImages[0].ordinal;

                if (imageUrl) return { imageUrl, caption, thumbnailImageUrl, imageOrdinal };
            }
        }

        return { imageUrl, caption, thumbnailImageUrl, imageOrdinal };
    }

    @computed
    public get getReservationStripePaymentRequestId() {
        const hasPendingPaymentRequest = this.model.stripePaymentRequests.find(p => p.hasPaid === false && p.propertyId === this.model.id && p.type === 1);

        if (hasPendingPaymentRequest) {
            return hasPendingPaymentRequest.id;
        }
        return "";
    }

    @computed
    public get getChoicesStripePaymentRequestId() {
        const hasPendingPaymentRequest = this.model.stripePaymentRequests.find(p => p.hasPaid === false && p.propertyId === this.model.id && p.type === 2);

        if (hasPendingPaymentRequest) {
            return hasPendingPaymentRequest.id;
        }
        return "";
    }

    @computed
    public get getChoicesStripePaymentRequest() {
        const hasPendingPaymentRequest = this.model.stripePaymentRequests.find(p => p.propertyId === this.model.id && p.type === 2 && p.hasPaid === false);

        if (hasPendingPaymentRequest) {
            return `£${hasPendingPaymentRequest.payment.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
        }
        return "-";
    }

    @computed
    public get getCompletedChoicesStripePaymentRequest() {
        const hasPendingPaymentRequest = this.model.stripePaymentRequests.find(p => p.propertyId === this.model.id && p.type === 2 && p.hasPaid === true);

        if (hasPendingPaymentRequest) {
            return `£${hasPendingPaymentRequest.payment.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
        }
        return "-";
    }

    @computed
    public get getChoicesListOutstanding() {
        const hasPendingPaymentRequests = this.model.stripePaymentRequests.filter(p => p.hasPaid === false && p.propertyId === this.model.id && p.type === 2);

        return hasPendingPaymentRequests;
    }

    @computed
    public get getOutstanding() {
        const hasPendingPaymentRequests = this.model.stripePaymentRequests.filter(p => p.hasPaid === false && p.propertyId === this.model.id);

        return hasPendingPaymentRequests;
    }

    @computed
    public get getPaymentPrice() {
        const hasPendingReservationPaymentRequest = this.model.stripePaymentRequests.find(p => p.hasPaid === false && p.propertyId === this.model.id && p.type === 1);
        const hasPendingChoicesPaymentRequest = this.model.stripePaymentRequests.find(p => p.hasPaid === false && p.propertyId === this.model.id && p.type === 2);
        let totalPrice: number = 0;
        if (hasPendingReservationPaymentRequest) {
            totalPrice += hasPendingReservationPaymentRequest.payment;
        }
        if (hasPendingChoicesPaymentRequest) {
            totalPrice += hasPendingChoicesPaymentRequest.payment;
        }
        return totalPrice;
    }

    @computed
    public get PropertyOverview() {
        return `${this.model.houseTypeName}, ${this.propertyTypeName} with ${this.numberOfBeds} bedrooms`;
    }

    @computed
    public get PropertyTitle() {
        return `Plot ${this.model.plotNumber} ${this.model.developmentName}`;
    }

    @computed
    public get CanDisplaySalesAgent() {
        return !isEmptyOrWhitespace(this.model.salesAgentName);
    }

    @computed
    public get CanDisplayAgreedPurchasePrice() {
        return this.model.agreedPurchasePrice > 0.0;
    }

    @computed
    public get AgreedPurchasePrice() {
        return `£${this.model.agreedPurchasePrice.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
    }

    @computed
    public get CanDisplayReservationFeePaid() {
        return this.model.reservationFeePaid > 0.0;
    }

    @computed
    public get ReservationFeePaid() {
        return `£${this.model.reservationFeePaid.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
    }

    // Build dates.
    @computed
    public get propertyTypeName() {
        let propertyTypeName = "";

        if (this.model.plotTypeName != "" && this.model.plotTypeName != null) {
            propertyTypeName = this.model.plotTypeName;
        } else {
            propertyTypeName = this.model.propertyTypeName;
        }

        return propertyTypeName;
    }

    @computed
    public get Description() {
        let description = "";

        if (this.model.plotDescription != "" && this.model.plotDescription != null) {
            description = this.model.plotDescription;
        } else {
            description = this.model.houseTypeDescription;
        }

        return description;
    }

    @computed
    public get numberOfBeds() {
        let numberOfBeds = 0;

        if (this.model.propertyNumberOfBedrooms != 0 && this.model.propertyNumberOfBedrooms != null) {
            numberOfBeds = this.model.propertyNumberOfBedrooms;
        } else {
            numberOfBeds = this.model.numberOfBedrooms;
        }

        return numberOfBeds;
    }

    @computed
    public get BuildActualDate() {
        return isNullOrUndefined(this.model.buildActualDate) ? "-" : this.model.buildActualDate.format("DD/MM/YYYY");
    }

    @computed
    public get showDatePicker() {
        const setting: GlobalSetting | undefined = StoresInstance.domain.AccountStore.getSettingByType(14);

        if (setting) {
            if (setting.settingValue === "0") {
                return false;
            } else {
                return true;
            }
        }

        return false;
    }

    @computed
    public get hasLegalStatus() {
        if (this.SalesStatusName) {
            if (this.SalesStatusName === "Legal completion" || this.SalesStatusName === "10 year warranty") {
                return true;
            }
        }

        return false;
    }

    @computed
    public get getWarrantyCountdownInYears() {
        const setting: GlobalSetting | undefined = StoresInstance.domain.AccountStore.getSettingByType(23);

        if (setting) {
            return Number(setting.settingValue);
        }

        return 10;
    }

    @computed
    public get BuildTargetDate() {
        let retVal: string = "";
        if (this.showDatePicker) {
            retVal = isNullOrUndefined(this.model.buildTargetDate) ? "-" : this.model.buildTargetDate.format("DD/MM/YYYY");
        } else {
            const month = this.model.buildTargetDateMonth ? this.model.buildTargetDateMonth : "";
            const year = this.model.buildTargetDateYear ? this.model.buildTargetDateYear : "";
            retVal = `${month} ${year}`;
        }
        return retVal;
    }

    // Choice dates
    @computed
    public get ChoicesActualDate() {
        if (this.model.hasOrder) {
            return isNullOrUndefined(this.model.orderCompletedDate) ? "-" : this.model.orderCompletedDate.format("DD/MM/YYYY");
        } else {
            return isNullOrUndefined(this.model.tempChoicesActualDate) ? "-" : this.model.tempChoicesActualDate.format("DD/MM/YYYY");
        }
    }

    @computed
    public get ChoicesTargetDate() {
        if (!this.model.hasReservationType1) {
            return isNullOrUndefined(this.model.tempChoicesTargetDate) ? "-" : this.model.tempChoicesTargetDate.format("DD/MM/YYYY");
        } else {
            return isNullOrUndefined(this.model.choicesTargetDate)
                ? isNullOrUndefined(this.model.tempChoicesTargetDate)
                    ? "-"
                    : this.model.tempChoicesTargetDate.format("DD/MM/YYYY")
                : this.model.choicesTargetDate.format("DD/MM/YYYY");
        }
    }

    // Contract dates
    @computed
    public get ContractActualDate() {
        return isNullOrUndefined(this.model.contractActualDate) ? "-" : this.model.contractActualDate.format("DD/MM/YYYY");
    }

    @computed
    public get ContractTargetDate() {
        return isNullOrUndefined(this.model.contractTargetDate) ? "-" : this.model.contractTargetDate.format("DD/MM/YYYY");
    }

    // Legal dates
    @computed
    public get LegalActualDate() {
        return isNullOrUndefined(this.model.legalActualDate) ? "-" : this.model.legalActualDate.format("DD/MM/YYYY");
    }

    @computed
    public get LegalTargetDate() {
        return isEmptyOrWhitespace(this.model.legalTargetMonth) || isEmptyOrWhitespace(this.model.legalTargetYear)
            ? "-"
            : `${this.model.legalTargetMonth} ${this.model.legalTargetYear}`;
    }

    // sales status
    @computed
    public get SalesStatusName() {
        return this.model.salesStatusName;
    }

    @computed
    public get oustandingPaymentsList(): OutstandingPayment[] {
        // return only items not already chosen
        return this.masterOutstandingPaymentsList.filter(item => !this.chosenOutstandingPaymentItem.some(chosen => chosen.id === item.id));
    }

    @computed
    public get havePaymentsOutstanding(): boolean {
        // if outstanding payments list has items and/or reservation fee not paid then return true
        if (this.masterOutstandingPaymentsList.length === 0 && !this.hasStripePaymentRequest) {
            return false;
        }

        return true;
    }

    @computed
    public get returnOutstandingReservationFee() {
        const hasOutstanding = this.masterOutstandingPaymentsList.find(o => o.source === ProductSourceType.reservationFee);

        if (hasOutstanding) {
            return `£${hasOutstanding.cost.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
        }

        return "-";
    }

    @computed
    public get returnOutstandingChoices() {
        // Find all items that belong to either category
        const items = this.masterOutstandingPaymentsList.filter(o => o.source === ProductSourceType.product || o.source === ProductSourceType.bespokeProduct);

        if (items.length === 0) return "-";

        const total = items.reduce((sum, item) => sum + item.cost, 0);

        return `£${total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
    }

    @computed
    public get returnOutstandingBalance() {
        // Find all items that belong to either category
        const items = this.masterOutstandingPaymentsList.filter(
            o => o.source === ProductSourceType.product || o.source === ProductSourceType.bespokeProduct || o.source === ProductSourceType.reservationFee,
        );

        if (items.length === 0) return "-";

        const total = items.reduce((sum, item) => sum + item.cost, 0);

        return `£${total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
    }

    @computed
    public get completedPaymentsList(): CompletedPayment[] {
        return this.masterCompletedPaymentsList.sort((a, b) => {
            const dateA = a.paidDate ? new Date(a.paidDate).getTime() : 0;
            const dateB = b.paidDate ? new Date(b.paidDate).getTime() : 0;
            if (dateA !== dateB) {
                return dateB - dateA; // Sort by date descending
            }
            // If dates are equal, sort by paymentRef descending
            if (a.paymentRef && b.paymentRef) {
                return b.paymentRef.localeCompare(a.paymentRef);
            }
            return 0;
        });
    }

    @computed
    public get completedPaymentsGrouped() {
        const normalizeRef = (ref?: string | null): string => {
            if (!ref || !ref.trim()) return ""; // treat null/empty as ""
            return ref.trim().toLowerCase();
        };

        const normalizeDate = (date?: string | null): string => {
            if (!date) return "";
            const d = new Date(date);
            if (isNaN(d.getTime())) return date; // if invalid, return raw
            return d.toISOString().split("T")[0]; // YYYY-MM-DD
        };

        const map = new Map<string, { paymentRef: string; paidDate: string; items: CompletedPayment[]; total: number }>();

        this.masterCompletedPaymentsList.forEach(item => {
            const normRef = normalizeRef(item.paymentRef);
            const normDate = normalizeDate(item.paidDate);

            // 🔥 Fallback grouping key for legacy payments
            const key = normRef !== "" ? `${normRef}|${normDate}` : `legacy|${normDate}`;

            if (!map.has(key)) {
                map.set(key, {
                    paymentRef: normRef !== "" ? item.paymentRef.trim() : "Legacy Payment",
                    paidDate: normDate,
                    items: [],
                    total: 0,
                });
            }

            const group = map.get(key)!;
            group.items.push(item);
            group.total += item.cost * (item.quantity ?? 1);
        });

        return Array.from(map.values()).sort((a, b) => {
            return new Date(b.paidDate).getTime() - new Date(a.paidDate).getTime();
        });
    }
    // #endregion Computeds

    // #region Actions

    @action
    public setApiStatus(apiStatus: ApiStatus) {
        this.apiStatus = apiStatus;
    }

    @action
    public setPaymentComplete() {
        const hasPaidPendingPaymentRequests = this.model.stripePaymentRequests.filter(p => p.hasPaid === false && p.propertyId === this.model.id);
        if (hasPaidPendingPaymentRequests) {
            hasPaidPendingPaymentRequests.forEach(hasPaidPendingPaymentRequest => {
                hasPaidPendingPaymentRequest.hasPaid = true;
            });
        }
    }

    public navigateToDashboard = () => {
        this.history.push(`/${this.propertyId}`);
    };

    public navigateToViewReservationArea = (propertyId: string) => {
        this.history.push(`/reservation/${propertyId}`);
    };

    private load = async (id: string): Promise<void> => {
        this.setApiStatus(ApiStatus.loading);

        try {
            const apiResult = await this.Get<PropertyModelDTO>(Server.Api.Dashboard.GetProperty + "/" + id);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);

                this.populateOutstandingPaymentItem();

                this.setMasterCompletedPaymentsList();

                this.setApiStatus(ApiStatus.success);
            } else {
                this.setApiStatus(ApiStatus.error);
            }
        } catch (exception) {
            this.setApiStatus(ApiStatus.error);
        } finally {
            // Finally
        }
    };

    private loadUserSignatureRole = async (propertyId: string, email: string): Promise<void> => {
        this.setApiStatus(ApiStatus.loading);

        try {
            const apiResult = await this.Post<{ role: string }>(Server.Api.Dashboard.GetUserSignatureRole, { email, propertyId });

            if (apiResult.wasSuccessful) {
                // Store role in userStore
                StoresInstance.domain.UserStore.setUserSignatureRole(apiResult.payload.role as "purchaserOne" | "purchaserTwo" | "none");
                this.setApiStatus(ApiStatus.success);
            } else {
                this.setApiStatus(ApiStatus.error);
            }
        } catch (exception) {
            console.error("Failed to load user signature role", exception);
            this.setApiStatus(ApiStatus.error);
        }
    };

    // populate outstanding payment item with ReservationFee, Bespoke Products and Products
    @action
    public populateOutstandingPaymentItem = () => {
        this.masterOutstandingPaymentsList = [];

        // Reservation Fee
        const reservationFee = this.model.stripePaymentRequests.find(
            p => !p.hasPaid && p.propertyId === this.model.id && p.type === 1 && p.hasPaid === false /*  && p.paymentRequested === true */,
        );
        if (reservationFee) {
            this.masterOutstandingPaymentsList.push({
                id: reservationFee.id!,
                name: "Reservation Fee",
                cost: reservationFee.payment,
                quantity: 1,
                source: ProductSourceType.reservationFee,
                paymentRequested: reservationFee.paymentRequested,
            });
        }

        // Bespoke Products
        this.model.orderAreaProductGroupProductsBespoke
            .filter(p => p.isPaid === false /*  && p.paymentRequested === true */)
            .filter(p => p.cost !== null && p.cost !== undefined && p.cost > 0) // Exclude zero/negative/POA costs
            .forEach(product => {
                this.masterOutstandingPaymentsList.push({
                    id: product.id,
                    name: product.name,
                    cost: product.cost,
                    quantity: 1,
                    source: ProductSourceType.bespokeProduct,
                    paymentRequested: product.paymentRequested,
                });
            });

        // Standard Products
        this.model.orderAreaProductGroupProducts
            .filter(p => p.isPaid === false /*  && p.paymentRequested === true */)
            .filter(p => p.cost !== null && p.cost !== undefined && (p.cost * p.quantity) > 0) // Exclude zero/negative/POA costs
            .forEach(product => {
                this.masterOutstandingPaymentsList.push({
                    id: product.id,
                    name: product.name,
                    cost: product.cost * product.quantity,
                    quantity: product.quantity,
                    source: ProductSourceType.product,
                    paymentRequested: product.paymentRequested,
                });
            });
    };

    @action
    public setChosenOutstandingPaymentItemIds = (ids: string[]) => {
        // lookup using the master list (source of truth)
        const rows = this.masterOutstandingPaymentsList.filter(item => ids.includes(item.id));
        this.chosenOutstandingPaymentItem = rows;
        this.selectedTotalToPay = rows.reduce((sum, item) => sum + item.cost, 0);
    };

    @action
    public removeChosenOutstandingPaymentItem = (row: OutstandingPayment) => {
        this.chosenOutstandingPaymentItem = this.chosenOutstandingPaymentItem.filter(item => item.id !== row.id);
        this.selectedTotalToPay = this.chosenOutstandingPaymentItem.reduce((sum, item) => sum + item.cost, 0);
    };

    @action
    public clearChosenOutstandingPaymentItems = () => {
        this.chosenOutstandingPaymentItem = [];
        this.selectedTotalToPay = 0;
    };

    @action
    public setMasterCompletedPaymentsList = () => {
        this.masterCompletedPaymentsList = [];

        // Reservation Fee
        const reservationFee = this.model.stripePaymentRequests.find(p => p.hasPaid && p.propertyId === this.model.id && p.type === 1 && p.hasPaid === true);
        if (reservationFee) {
            this.masterCompletedPaymentsList.push({
                id: reservationFee.id!,
                name: "Reservation Fee",
                cost: reservationFee.payment,
                quantity: 1,
                source: ProductSourceType.reservationFee,
                paidDate: reservationFee.paidDate,
                paymentRef: reservationFee.referenceNo,
            });
        }

        // Bespoke Products
        this.model.orderAreaProductGroupProductsBespoke
            .filter(p => p.isPaid === true)
            .forEach(product => {
                this.masterCompletedPaymentsList.push({
                    id: product.id,
                    name: product.name,
                    cost: product.cost,
                    quantity: 1,
                    source: ProductSourceType.bespokeProduct,
                    paidDate: product.isPaidDate,
                    paymentRef: product.paymentRef,
                });
            });

        // Standard Products
        this.model.orderAreaProductGroupProducts
            .filter(p => p.isPaid === true)
            .forEach(product => {
                this.masterCompletedPaymentsList.push({
                    id: product.id,
                    name: product.name,
                    cost: product.cost * product.quantity,
                    quantity: product.quantity,
                    source: ProductSourceType.product,
                    paidDate: product.isPaidDate,
                    paymentRef: product.paymentRef,
                });
            });
    };

    //format currency string
    public formatCurrency = (amount: number | string): string => {
        const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;

        if (isNaN(numericAmount)) {
            return "£0.00"; // or throw an error, depending on your needs
        }

        return `£${numericAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
    };

    // #endregion Actions

    // #region Functions
    public formatItemDate = (date: string): string => {
        if (date) {
            let dateMoment = moment(date);

            return dateMoment.format("DD/MM/YY");
        } else {
            return "-";
        }
    };
    // #endregion Functions

    // #region Boilerplate

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    public isFieldValid(fieldName: keyof FieldType<any>): boolean {
        return true;
    }

    // #endregion Boilerplate
}
